import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Header from './components/Header';
import ObjectMeterManager from './components/ObjectMeterManager';
import ReadingInput from './components/ReadingInput';
import ReadingsList from './components/ReadingsList';
import AnalysisOverlay from './components/AnalysisOverlay';
import MagicDownload from './components/MagicDownload';
import KnowledgeSection from './components/KnowledgeSection';
import AdBanner from './components/AdBanner';
import Footer from './components/Footer';
import { useEnergyData } from './hooks/useEnergyData';
import { COLORS } from './utils/constants';

const { FiZap, FiActivity, FiDroplet } = FiIcons;

function App() {
  const { 
    data, 
    addObject,
    updateObject,
    removeObject, 
    addMeter, 
    updateMeter,
    deleteMeter,
    addReading, 
    updateReading,
    deleteReading,
    duplicateReading,
    setCompanyName,
    incrementStat,
    importData
  } = useEnergyData();
  
  const [selectedObjId, setSelectedObjId] = useState(data.objects[0]?.id);
  const [selectedMeterId, setSelectedMeterId] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [selectedReadingsForAnalysis, setSelectedReadingsForAnalysis] = useState([]);
  const [isFirstReading, setIsFirstReading] = useState(true);

  const activeObject = data.objects.find(o => o.id === selectedObjId) || data.objects[0];
  const activeMeter = activeObject?.meters.find(m => m.id === selectedMeterId) || activeObject?.meters[0];

  // Auto-select first meter
  useEffect(() => {
    if (activeObject && (!activeMeter || !activeObject.meters.find(m => m.id === activeMeter?.id))) {
      setSelectedMeterId(activeObject.meters[0]?.id);
    }
  }, [activeObject, activeMeter]);

  const handleAddReading = (reading) => {
    addReading(activeObject.id, activeMeter.id, reading);
    
    // Confetti on first reading
    if (isFirstReading && activeMeter.readings?.length === 0) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      setIsFirstReading(false);
    }
  };

  const handleAnalyze = (readings) => {
    setSelectedReadingsForAnalysis(readings);
    setShowAnalysis(true);
    incrementStat('analyses');
  };

  const getMeterIcon = (type) => {
    switch(type) {
      case 'electricity': return FiZap;
      case 'gas': return FiActivity;
      case 'water':
      case 'waterCold':
      case 'waterWarm':
        return FiDroplet;
      default: return FiActivity;
    }
  };

  const getMeterColor = (type) => {
    return COLORS[type] || COLORS.primary;
  };

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: COLORS.background }}>
      <Header />
      
      <main className="container mx-auto px-4 py-6 max-w-5xl space-y-8">
        
        {/* Object & Meter Manager */}
        <ObjectMeterManager 
          objects={data.objects}
          activeObjId={selectedObjId}
          activeMeterId={selectedMeterId}
          companyName={data.companyName || ''}
          onSelectObj={setSelectedObjId}
          onSelectMeter={setSelectedMeterId}
          onAddObj={addObject}
          onUpdateObj={updateObject}
          onDeleteObj={removeObject}
          onAddMeter={addMeter}
          onUpdateMeter={updateMeter}
          onDeleteMeter={deleteMeter}
          onSetCompanyName={setCompanyName}
        />

        {activeMeter ? (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Meter Header */}
            <div className="flex items-center gap-3 text-xl font-bold" style={{ color: COLORS.primary }}>
              <motion.div 
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="p-3 rounded-full text-white shadow-lg"
                style={{ backgroundColor: getMeterColor(activeMeter.type) }}
              >
                <SafeIcon icon={getMeterIcon(activeMeter.type)} size={24} />
              </motion.div>
              <span>{activeMeter.name}</span>
            </div>

            {/* Reading Input */}
            <ReadingInput 
              meter={activeMeter}
              onAddReading={handleAddReading}
            />

            {/* Readings List & Analysis */}
            <ReadingsList 
              meter={activeMeter}
              objectId={activeObject.id}
              onAnalyze={handleAnalyze}
              onUpdateReading={updateReading}
              onDeleteReading={deleteReading}
              onDuplicateReading={duplicateReading}
            />

          </motion.div>
        ) : (
          <div className="text-center py-10 text-gray-500 bg-white rounded-xl shadow-lg p-6">
            <p className="text-lg">Bitte legen Sie zuerst einen Zähler an</p>
            <p className="text-sm text-gray-400 mt-2">(z.B. "Strom Hauptzähler", "Gas Heizung", "Wasser")</p>
          </div>
        )}

        {/* Magic Download */}
        <MagicDownload 
          data={data}
          activeObject={activeObject}
          activeMeter={activeMeter}
          onDownload={() => incrementStat('magicDownloads')}
          onImport={importData}
        />

        {/* Knowledge & Tips */}
        <KnowledgeSection />

        {/* Ad Banner */}
        <AdBanner onClick={() => incrementStat('adClicks')} />
        
        {/* Footer */}
        <Footer />

      </main>

      {/* Analysis Overlay */}
      <AnimatePresence>
        {showAnalysis && selectedReadingsForAnalysis.length === 2 && (
          <AnalysisOverlay 
            readings={selectedReadingsForAnalysis}
            meter={activeMeter}
            onClose={() => setShowAnalysis(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;